import React from 'react';

export type CheckoutStateTone = 'progress' | 'success' | 'error' | 'neutral';

export interface CheckoutStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface CheckoutStateCardProps {
  /** Drives the glyph colour when `figure` is `glyph`. */
  tone?: CheckoutStateTone;
  /**
   * `spinner` waiting on the PSP · `skeleton` first paint · `bar` ready ·
   * `glyph` terminal states · or any custom node (e.g. an inline field error).
   */
  figure?: 'spinner' | 'skeleton' | 'bar' | 'glyph' | 'none' | React.ReactNode;
  /** Single character shown inside the glyph badge. */
  glyph?: string;
  heading: string;
  body?: string;
  primary?: CheckoutStateAction;
  secondary?: CheckoutStateAction;
  /** Card shadow + hairline border. Off when the card already sits inside a framed shell. */
  framed?: boolean;
  /** Small line under the actions — used for the live hold countdown. */
  note?: React.ReactNode;
}

const GLYPH_TONE: Record<CheckoutStateTone, string> = {
  progress: 'ck-glyph--neutral',
  success: 'ck-glyph--success',
  error: 'ck-glyph--error',
  neutral: 'ck-glyph--neutral',
};

function renderFigure(
  figure: CheckoutStateCardProps['figure'],
  tone: CheckoutStateTone,
  glyph?: string
): React.ReactNode {
  if (figure === 'none' || figure === undefined) return null;

  if (figure === 'spinner') {
    return <span className="ck-spin" style={{ width: 38, height: 38 }} aria-hidden="true" />;
  }

  if (figure === 'skeleton') {
    return (
      <span className="ck-skeleton" aria-hidden="true">
        <span style={{ width: '72%' }} />
        <span style={{ width: '90%' }} />
        <span style={{ width: '54%' }} />
      </span>
    );
  }

  if (figure === 'bar') {
    return (
      <span className="ck-bar" aria-hidden="true">
        <span style={{ width: '100%' }} />
      </span>
    );
  }

  if (figure === 'glyph') {
    return (
      <span className={`ck-glyph ${GLYPH_TONE[tone]}`} aria-hidden="true">
        {glyph || '•'}
      </span>
    );
  }

  return figure;
}

function Action({ action, kind }: { action: CheckoutStateAction; kind: 'primary' | 'secondary' }) {
  const className = `ck-state-btn ck-state-btn--${kind}`;
  if (action.href) {
    return (
      <a className={className} href={action.href} onClick={action.onClick}>
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={action.onClick}>
      {action.label}
    </button>
  );
}

/**
 * One checkout state — the shared card behind every non-form moment of the flow:
 * loading, submitting, 3-D Secure wait, failure, expiry, success and the popup-blocked
 * fallback. Same component in the widget frame and in the hosted page's payment column.
 */
const CheckoutStateCard: React.FC<CheckoutStateCardProps> = ({
  tone = 'progress',
  figure = 'none',
  glyph,
  heading,
  body,
  primary,
  secondary,
  framed = false,
  note,
}) => {
  const fig = renderFigure(figure, tone, glyph);
  const live = tone === 'progress' ? 'polite' : undefined;

  return (
    <div className={`ck-state${framed ? ' ck-state--framed' : ''}`} aria-live={live}>
      {fig ? <div className="ck-state-figure">{fig}</div> : null}
      <h3 className="ck-state-heading">{heading}</h3>
      {body ? <p className="ck-state-body">{body}</p> : null}
      {(primary || secondary) && (
        <div className="ck-state-actions">
          {primary && <Action action={primary} kind="primary" />}
          {secondary && <Action action={secondary} kind="secondary" />}
        </div>
      )}
      {note ? <div className="ck-secure">{note}</div> : null}
    </div>
  );
};

export default CheckoutStateCard;
